import React, {useEffect, useState} from "react";
import {Alert, Avatar, Badge, Button, Card, Descriptions, Divider, List, Space, Spin, Tag, Typography,} from "antd";

import {Link, useParams} from "react-router-dom";
import {useApiReadStudentLessonId} from "@/api/lesson";
import {Award, BookOpen, Calendar, ClipboardList, Clock, User, Users} from 'lucide-react';
import {lessonGenderNames, teacherGenderNames, weekdayNames} from "@/pages/student/lessons/take/_section/LessonCard";
import toFaNumber from "@/lib/utils/number";
import StudentLayout from "@/components/layout/student/studentLayout";
import RenderIf from "@/components/render-if";
import LessonPricingSection from "@/pages/student/lessons/take/[id]/_section/LessonPricingSection";
import {URLS} from "@/lib/urls";
import LessonRequirementSection from "@/pages/student/lessons/take/[id]/_section/LessonRequirementSection";
import AgeMessage from "@/components/age/age-message";
import IranMap from "@/components/map/Map";

const {Title, Text} = Typography;


function MapInTakePopupElement({item}) {
    return (<>
        <div className="flex flex-col gap-4 items-center peydaFont">

            <div className="font-bold text-lg">{item?.title}</div>
            <div>
                <Link to={`https://nshn.ir/?lat=${item?.latitude}&lng=${item?.longitude}`}
                      target={`_blank`}
                >
                    <Button type="primary" size={'small'}>
                        مسیریابی با نشان
                    </Button>
                </Link>
            </div>
        </div>

    </>);
}

const ClassSelectionCard = () => {

    const [lessonDetail, setLessonDetail] = useState({})

    const param = useParams();

    const [mapPoints, setMapPoints] = useState([])

    const {data, isPending, refetch} = useApiReadStudentLessonId(param.id)


    useEffect(() => {
        if (data?.data?.data) {
            setLessonDetail(data?.data?.data)
            let classrooms = [];
            data?.data?.data?.times?.map((item) => {
                if (item?.classroom) {
                    if (item?.classroom?.latitude && item?.classroom?.longitude) {
                        classrooms.push(item?.classroom);
                    }
                }
            })
            setMapPoints(classrooms);
        }
    }, [data, param.id])


    if (isPending) {
        return (<StudentLayout>
            <div className={'text-center'}>
                <Spin spinning={isPending}></Spin>
            </div>
        </StudentLayout>);
    }

    const weekOrder = {
        saturday: 0,
        sunday: 1,
        monday: 2,
        tuesday: 3,
        wednesday: 4,
        thursday: 5,
        friday: 6,
    };

    const sortSchedule = (times) => {

        if (!times) {
            return []
        }

        return [...times].sort(
            (a, b) => weekOrder[a.weekday] - weekOrder[b.weekday]
        );
    };

    return (
        <StudentLayout pageTitle={'انتخاب درس'} backTo={true}>

            <RenderIf condition={lessonDetail?.takenunits && lessonDetail?.takenunits?.length > 0}>
                <Alert type="success"
                       message={'شما قبلا در این درس ثبت‌نام کرده‌اید'}
                       description={<Link to={URLS.student.lessons.myLesson.list}>برای مشاهده درس‌های ثبت‌نام کرده اینجا
                           کلیک
                           کنید</Link>}
                       showIcon={true}>
                </Alert>
            </RenderIf>

            <div className="p-1">
                <Card className="mb-6">
                    <Title level={3} className="text-blue-600 mb-4">
                        {lessonDetail?.topic?.title}
                        <br/>
                        <Badge text={' کد درس: ' + toFaNumber(lessonDetail?.code)} color="purple"/>
                    </Title>

                    <RenderIf condition={lessonDetail?.title}>
                        <Alert
                            description={lessonDetail?.title}
                            type="info"
                            showIcon
                            icon={<ClipboardList/>}
                            className="mb-6"
                        />
                        <Divider/>
                    </RenderIf>

                    <RenderIf condition={lessonDetail?.teacher}>
                        <div className="flex items-center space-x-4 space-x-reverse my-2">
                            <Avatar src={lessonDetail?.teacher_detail?.avatar} size={64} className="bg-blue-500">
                                <User className="w-8 h-8"/>
                            </Avatar>
                            <div>
                                <Title level={4} className="mb-1">
                                    {teacherGenderNames[lessonDetail?.teacher_detail?.gender]} {' '}
                                    {lessonDetail?.teacher_detail?.firstname} {lessonDetail?.teacher_detail?.lastname}
                                </Title>
                                <Text type="secondary">استاد دوره</Text>
                            </div>
                        </div>
                        <Divider/>
                    </RenderIf>


                    <RenderIf condition={!!lessonDetail?.topic?.grade?.desc || !!lessonDetail?.topic?.desc}>
                        <Title level={5}>توضیحات دوره</Title>
                        <RenderIf condition={!!lessonDetail?.topic?.desc}>
                            <div className=" leading-relaxed mb-4">
                                {lessonDetail?.topic?.desc}
                            </div>
                        </RenderIf>
                        <RenderIf condition={!!lessonDetail?.topic?.group?.desc}>
                            <Tag color="orange" className=" px-3 py-1">
                                <Users className="w-4 h-4 inline ml-1"/>
                                {lessonDetail?.topic?.group.title}
                            </Tag>
                            <div className=" leading-relaxed mb-6">
                                {lessonDetail?.topic?.group?.desc}
                            </div>
                        </RenderIf>

                        <RenderIf condition={!!lessonDetail?.topic?.grade?.desc}>
                            <Tag color="green" className=" px-3 py-1">
                                <Award className="w-4 h-4 inline ml-1"/>
                                {lessonDetail?.topic?.grade.title}
                            </Tag>
                            <div className=" leading-relaxed mb-6">
                                {lessonDetail?.topic?.grade?.desc}
                            </div>
                        </RenderIf>
                        <RenderIf condition={!!lessonDetail?.topic?.course?.desc}>
                            <Tag color="blue" className="px-3 py-1">
                                <BookOpen className="w-4 h-4 inline ml-1"/>
                                {lessonDetail?.topic?.course.title}
                            </Tag>
                            <div className=" leading-relaxed mb-6">
                                {lessonDetail?.topic?.course?.desc}
                            </div>
                        </RenderIf>
                        <Divider/>
                    </RenderIf>

                    <RenderIf condition={!!(lessonDetail?.times && lessonDetail?.times?.length > 0)}>
                        <Title level={5}>برنامه زمانی کلاس</Title>


                        <List

                            dataSource={sortSchedule(lessonDetail?.times)}
                            renderItem={(time) => (
                                <List.Item className="rounded-lg p-4 mb-3">
                                    <List.Item.Meta
                                        avatar={<Calendar className="w-6 h-6 text-blue-500"/>}
                                        title={
                                            <Text strong className="">
                                                {weekdayNames[time.weekday]}
                                            </Text>
                                        }
                                        description={
                                            <Space>
                                                <Clock className="w-4 h-4 "/>
                                                <Text>{toFaNumber(time.start)} تا {toFaNumber(time.end)}</Text>
                                                <Tag color="blue">{time.classroom.title}</Tag>
                                            </Space>
                                        }
                                    />
                                </List.Item>
                            )}
                        />


                        <RenderIf condition={mapPoints?.length > 0}>
                            <div className={'h-64 w-full'}>

                                <div className={'my-2 font-bold'}>موقعیت محل برگزاری کلاس را می‌توانید از روی نقشه
                                    مشاهده کنید
                                </div>

                                <IranMap
                                    lockPoint={mapPoints.at(0)}
                                    clickHandler={false}
                                    popupElement={<MapInTakePopupElement/>}
                                    myLocationDetector={true}
                                />
                            </div>
                        </RenderIf>


                        <Divider/>

                    </RenderIf>

                    <Descriptions title={'جزئیات بیشتر'}>

                        <Descriptions.Item label="جنسیت">
                            {lessonGenderNames[lessonDetail?.gender]}
                        </Descriptions.Item>

                        <Descriptions.Item label="ظرفیت کل">
                            {toFaNumber(lessonDetail?.maxperson)} نفر
                        </Descriptions.Item>
                        <Descriptions.Item label="تعداد افراد ثبت‌نام کرده">
                            {toFaNumber(lessonDetail?.takenunits_count ?? 0)} نفر
                        </Descriptions.Item>

                        <Descriptions.Item label="ظرفیت باقیمانده">
                            {toFaNumber(lessonDetail?.capacity ?? 0)} نفر
                        </Descriptions.Item>

                        <Descriptions.Item label="مدت زمان دوره">
                            <RenderIf condition={!!lessonDetail?.topic?.totaltime} falsyElement={'-'}>
                                {toFaNumber(lessonDetail?.topic?.totaltime)} ساعت
                            </RenderIf>
                        </Descriptions.Item>

                        <Descriptions.Item label="نمره قبولی">
                            <RenderIf condition={!!lessonDetail?.topic?.passscore} falsyElement={'-'}>
                                {toFaNumber(lessonDetail?.topic?.passscore)}
                            </RenderIf>
                        </Descriptions.Item>
                        <Descriptions.Item label="رده سنی مجاز">

                            <RenderIf condition={(!!lessonDetail?.topic?.minage || !!lessonDetail?.topic?.maxage)
                                || (!!lessonDetail?.topic?.minagedate || !!lessonDetail?.topic?.maxagedate)
                            }>
                                <AgeMessage
                                    minage={lessonDetail?.topic?.minage}
                                    maxage={lessonDetail?.topic?.maxage}
                                    minagedate={lessonDetail?.topic?.minagedate}
                                    maxagedate={lessonDetail?.topic?.maxagedate}
                                />

                            </RenderIf>


                            <RenderIf
                                condition={(!!lessonDetail?.topic?.grade?.minage || !!lessonDetail?.topic?.grade?.maxage) || (!!lessonDetail?.topic?.grade?.minagedate || !!lessonDetail?.topic?.grade?.maxagedate)}>

                                <AgeMessage
                                    minage={lessonDetail?.topic?.grade?.minage}
                                    maxage={lessonDetail?.topic?.grade?.maxage}
                                    minagedate={lessonDetail?.topic?.grade?.minagedate}
                                    maxagedate={lessonDetail?.topic?.grade?.maxagedate}
                                />
                            </RenderIf>


                        </Descriptions.Item>
                    </Descriptions>
                </Card>

                <LessonRequirementSection lessonDetail={lessonDetail} refetch={refetch}/>
                <LessonPricingSection lessonDetail={lessonDetail}/>


            </div>
        </StudentLayout>
    );
};

export default ClassSelectionCard;

